import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axiosConfig';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePositions } from '../utils/positions';

// Lista de métricas (claves para DB)
const metrics = [
  'altura',
  'altura_con_zapatos',
  'envergadura',
  'alcance',
  'salto_vertical_maximo',
  'salto_vertical_de_pie',
  'peso',
  'grasa_corporal',
  'longitud_mano',
  'ancho_mano',
  'press_banca',
  'agilidad',
  'sprint_cuarto',
];

// Lista de estadísticas (claves para DB)
const stats = [
  'games',
  'minutes',
  'points',
  'two_fg',
  'two_fg_percent',
  'three_fg',
  'three_fg_percent',
  'free_throws',
  'free_throw_percent',
  'rebounds_off',
  'rebounds_def',
  'total_rebounds',
  'assists',
  'personal_fouls',
  'blocks',
  'steals',
  'turnovers',
  'fouls_drawn',
  'ranking',
];

// Claves de colores para traducción
const colorKeys = [
  'darkGreen',
  'lightGreen',
  'yellow',
  'lightOrange',
  'darkRed',
];

// Mapeo clave de color -> color de fondo (constante, no depende del idioma)
const colorBackgrounds = {
  'darkGreen':    '#008000',
  'lightGreen':   '#90EE90',
  'yellow':       '#FFFF00',
  'lightOrange':  '#FFA07A',
  'darkRed':      '#8B0000',
};

// ------------------------------
// 2. Componente principal
// ------------------------------
export default function ColorRangeInterface() {
  const { t } = useTranslation();
  const { positionsWithKeys, getPosition } = usePositions(t);

  // Estado para todos los rangos de color desde la API
  const [allColorRanges, setAllColorRanges] = useState([]);

  // Estado para la posición seleccionada
  const [selectedPosition, setSelectedPosition] = useState('');
  // Métrica seleccionada
  const [selectedMetric, setSelectedMetric] = useState('');
  // Estadística seleccionada
  const [selectedStat, setSelectedStat] = useState('');

  // Datos de las 5 filas que se muestran
  const [rowsData, setRowsData] = useState([]);

  // Fetch de todos los color-ranges
  const fetchColorRanges = useCallback(async () => {
    try {
      const response = await api.get('/api/color-ranges');
      setAllColorRanges(response.data);
    } catch (error) {
      console.error(t('colorRange.loadError'), error);
    }
  }, [t]);

  useEffect(() => {
    fetchColorRanges();
  }, [fetchColorRanges]);

  // Reconstruye rowsData cuando cambian posición, métrica o estadística
  useEffect(() => {
    const selectedValue = selectedMetric || selectedStat;
    if (!selectedPosition || !selectedValue) {
      setRowsData([]);
      return;
    }

    const filteredData = allColorRanges.filter(
      item =>
        item.position === selectedPosition &&
        item.metric === selectedValue
    );

    const newRows = colorKeys.map(colorKey => {
      // Buscar por la clave del color (guardada en DB)
      const found = filteredData.find(f => f.color_label === colorKey);
      return {
        color_key:    colorKey,
        color_label:  t(`colorRange.colors.${colorKey}`),
        id:           found ? found.id : null,
        min_value:    found ? found.min_value : '',
        max_value:    found ? found.max_value : ''
      };
    });

    setRowsData(newRows);
  }, [selectedPosition, selectedMetric, selectedStat, allColorRanges, t]);

  // Cambios en min/max
  const handleChange = (index, field, value) => {
    setRowsData(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Autollenado de min tomando el max de la fila siguiente
  const handleFocus = (index, field) => {
    if (field === 'min_value' && index < rowsData.length - 1) {
      const below = rowsData[index + 1];
      if (below.max_value !== '' && rowsData[index].min_value === '') {
        setRowsData(prev => {
          const c = [...prev];
          c[index] = { ...c[index], min_value: below.max_value };
          return c;
        });
      }
    }
  };

  // Guardar (PUT o POST)
  const handleSaveRange = async index => {
    const row = rowsData[index];
    if (!row.min_value || !row.max_value) {
      alert(t('colorRange.completeFields'));
      return;
    }
    if (Number(row.min_value) >= Number(row.max_value)) {
      alert(t('colorRange.minLessThanMax'));
      return;
    }

    const selectedValue = selectedMetric || selectedStat;
    if (!selectedPosition || !selectedValue) return;

    const payload = {
      position: selectedPosition,
      metric: selectedValue,
      min_value: row.min_value,
      max_value: row.max_value,
      color_label: row.color_key // Guardar la clave del color, no la traducción
    };

    try {
      if (row.id) {
        await api.put(
          `/api/color-ranges/${row.id}`,
          payload
        );
        alert(t('colorRange.rangeUpdated', { id: row.id }));
      } else {
        const res = await api.post(
          '/api/color-ranges',
          payload
        );
        const newId = res.data.colorRangeId;
        setRowsData(prev => {
          const c = [...prev];
          c[index].id = newId;
          return c;
        });
        alert(t('colorRange.newRangeCreated'));
      }
      fetchColorRanges();
    } catch (err) {
      console.error(err);
      alert(t('colorRange.saveError'));
    }
  };

  // Condición para mostrar la tabla
  const selectedValue = selectedMetric || selectedStat;
  const showTable = selectedPosition && selectedValue;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('colorRange.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {/* Puesto */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="select-position-label">{t('colorRange.position')}</InputLabel>
          <Select
            labelId="select-position-label"
            value={selectedPosition}
            label={t('colorRange.position')}
            onChange={e => setSelectedPosition(e.target.value)}
          >
            <MenuItem value=""><em>{t('colorRange.none')}</em></MenuItem>
            {positionsWithKeys.map(pos => (
              <MenuItem key={pos.key} value={pos.key}>{pos.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Métrica */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="select-metric-label">{t('colorRange.metric')}</InputLabel>
          <Select
            labelId="select-metric-label"
            value={selectedMetric}
            label={t('colorRange.metric')}
            onChange={e => {
              setSelectedMetric(e.target.value);
              setSelectedStat(''); // reset estadística
            }}
          >
            <MenuItem value=""><em>{t('colorRange.noneF')}</em></MenuItem>
            {metrics.map(m => (
              <MenuItem key={m} value={m}>{t(`colorRange.metrics.${m}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Estadística */}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="select-stat-label">{t('colorRange.stat')}</InputLabel>
          <Select
            labelId="select-stat-label"
            value={selectedStat}
            label={t('colorRange.stat')}
            onChange={e => {
              setSelectedStat(e.target.value);
              setSelectedMetric(''); // reset métrica
            }}
          >
            <MenuItem value=""><em>{t('colorRange.noneF')}</em></MenuItem>
            {stats.map(s => (
              <MenuItem key={s} value={s}>
                {t(`colorRange.stats.${s}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tabla de color ranges */}
      {showTable && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('colorRange.tableTitle')} {getPosition(selectedPosition)} –{' '}
            {selectedStat
              ? t(`colorRange.stats.${selectedStat}`)
              : t(`colorRange.metrics.${selectedMetric}`)}
          </Typography>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('colorRange.color')}</TableCell>
                <TableCell>{t('colorRange.minimum')}</TableCell>
                <TableCell>{t('colorRange.maximum')}</TableCell>
                <TableCell>{t('colorRange.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsData.map((row, i) => (
                <TableRow key={row.color_key}>
                  <TableCell
                    sx={{
                      backgroundColor: colorBackgrounds[row.color_key],
                      color: '#000',
                      width: '200px'
                    }}
                  >
                    {row.color_label}
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={row.min_value}
                      onFocus={() => handleFocus(i, 'min_value')}
                      onChange={e =>
                        handleChange(i, 'min_value', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={row.max_value}
                      onFocus={() => handleFocus(i, 'max_value')}
                      onChange={e =>
                        handleChange(i, 'max_value', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      onClick={() => handleSaveRange(i)}
                    >
                      {t('colorRange.save')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
